import React, { FC } from 'react';
import ReactModal from 'react-modal';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import color from 'color';

import { ThemeProps } from '@styles/theme';
import { flex } from '@resources/mixins';
import { useTheme } from '@mui/material/styles';

type ModalProps = {
    isOpen: boolean;
    children: React.ReactNode;
    className?: string;
    overlayClassName?: string;
    shouldCloseOnOverlayClick?: boolean;
    shouldCloseOnEsc?: boolean;
    noPadding?: boolean;
    // On phone-sized viewports form modals take the whole screen; small
    // dialogs (confirmations, alerts) should pass 'centered' to stay floating.
    phoneVariant?: 'fullscreen' | 'centered';
    onRequestClose: () => void;
};

const useStyles = createUseStyles((theme: ThemeProps) => ({
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        ...flex('', 'center', 'center'),
        padding: theme.spacing(4),
        overflowY: 'auto',
        boxSizing: 'border-box',
        zIndex: 1000,
        backgroundColor: `${color(theme.colors.violet_darkest).fade(0.37)}`,
        [theme.down('tablet')]: {
            alignItems: 'flex-start',
            padding: theme.spacing(2),
        },
    },
    modal: {
        minHeight: 200,
        outline: 'none',
        position: 'relative',
        width: 'auto',
        maxWidth: 'calc(100vw - 32px)',
        maxHeight: 'calc(100dvh - 32px)',
        fallbacks: { maxHeight: 'calc(100vh - 32px)' },
        overflow: 'auto',
        borderRadius: theme.radius,
        border: `1.5px solid ${theme.colors.grey_light}`,
        backgroundColor: `${color(theme.colors.white)}`,
        boxShadow: '0px 8px 24px 4px rgba(0, 0, 0, 0.15)',
        backdropFilter: 'blur(10px) brightness(160%)',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        [theme.down('tablet')]: {
            width: 'auto',
            maxWidth: 'calc(100vw - 16px)',
            maxHeight: 'calc(100dvh - 16px)',
            fallbacks: { maxHeight: 'calc(100vh - 16px)' },
            marginTop: theme.spacing(1),
            marginBottom: theme.spacing(1),
        },
    },
    overlayFullscreenPhone: {
        [theme.down('mobile')]: {
            padding: 0,
        },
    },
    modalFullscreenPhone: {
        [theme.down('mobile')]: {
            width: '100vw !important',
            maxWidth: '100vw !important',
            height: '100dvh !important',
            maxHeight: '100dvh !important',
            fallbacks: [
                { height: '100vh !important' },
                { maxHeight: '100vh !important' },
            ],
            margin: 0,
            borderRadius: 0,
            border: 'none',
            paddingBottom: 'env(safe-area-inset-bottom)',
        },
    },
    modalInner: {
        padding: theme.spacing(0),
        position: 'relative',
        width: '100%',
        height: '100%',
        maxHeight: 'inherit',
        overflow: 'auto',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        [theme.down('tablet')]: {
            height: 'auto',
            minHeight: '100%',
        },
    },
    modalInnerPadding: {
        padding: theme.spacing(8),
        position: 'relative',
        height: '100%',
        width: '100%',
        maxHeight: 'inherit',
        overflow: 'auto',
        boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        [theme.down('tablet')]: {
            padding: theme.spacing(5),
            height: 'auto',
            minHeight: '100%',
        },
    },
}));

export const Modal: FC<ModalProps> = props => {
    const { children, overlayClassName, className, phoneVariant = 'fullscreen', ...rest } = props;
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });
    const isFullscreenPhone = phoneVariant === 'fullscreen';

    return (
        <ReactModal
            overlayClassName={classNames(styles.overlay, overlayClassName, {
                [styles.overlayFullscreenPhone]: isFullscreenPhone,
            })}
            className={classNames(styles.modal, className, {
                [styles.modalFullscreenPhone]: isFullscreenPhone,
            })}
            {...rest}
        >
            <div className={props.noPadding ? styles.modalInner : styles.modalInnerPadding}>
                {children}
            </div>
        </ReactModal>
    )
}
